// utils/remoteConfig.ts
// Utility to fetch API keys from Firebase Remote Config

import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
} from "firebase/remote-config";
import type { FirebaseApp } from "firebase/app";

let categoryImageMap: Record<string, string[]> = {};
let rcInitPromise: Promise<void> | null = null;

export function setCategoryImageMap(map: Record<string, string[]>) {
  categoryImageMap = map || {};
}

export function getCategoryImageMap(): Record<string, string[]> {
  return categoryImageMap;
}

export function initRemoteConfig(app?: FirebaseApp) {
  if (!rcInitPromise) {
    rcInitPromise = fetchRemoteConfigApiKeys(app)
      .then((res) => {
        setCategoryImageMap(res.CATEGORY_IMAGES || {});
      })
      .catch((err) => {
        rcInitPromise = null;
        throw err;
      });
  }
  return rcInitPromise;
}

export async function fetchRemoteConfigApiKeys(app?: FirebaseApp) {
  // Dynamically import app if not provided (to avoid circular dependency)
  let firebaseApp = app;
  if (!firebaseApp) {
    const firebase = await import("../services/firebase");
    firebaseApp = firebase.app;
  }
  const remoteConfig = getRemoteConfig(firebaseApp);
  remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
  await fetchAndActivate(remoteConfig);

  let normalizedCategoryImages: Record<string, string[]> = {};
  // category images stored as JSON object mapping category -> string[] URLs
  const rawCategoryImages = getValue(remoteConfig, "CATEGORY_IMAGES").asString();
  try {
    const parsed = JSON.parse(rawCategoryImages || "null");
    if (parsed && typeof parsed === "object") {
      // Normalize to string[] per category
      const normalized: Record<string, string[]> = {};
      Object.entries(parsed as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (Array.isArray(value)) {
            const cleaned = value
              .map((v) => (typeof v === "string" ? v.trim() : ""))
              .filter(Boolean);
            if (cleaned.length > 0) normalized[key] = cleaned;
          } else if (typeof value === "string" && value.trim()) {
            normalized[key] = [value.trim()];
          }
        },
      );
      normalizedCategoryImages = normalized;
      setCategoryImageMap(normalized);
    }
  } catch (err) {
    console.warn(
      "Remote Config CATEGORY_IMAGES parse error:",
      (err as any)?.message || err,
    );
  }

  return {
    GOOGLE_API_KEY: getValue(remoteConfig, "GOOGLE_API_KEY").asString(),
    TMDB_API_KEY: getValue(remoteConfig, "TMDB_API_KEY").asString(),
    UNSPLASH_ACCESS_KEY: getValue(
      remoteConfig,
      "UNSPLASH_ACCESS_KEY",
    ).asString(),
    CATEGORY_IMAGES: normalizedCategoryImages,
  };
}
